<template>
  <div title="TextPuzzle interaction" class="question-node textpuzzle-node" :class="{ selected: isSelected, collapsed: isCollapsed }">
    <!-- En-tête -->
    <div class="node-header">
      <button @click="toggleCollapse" class="collapse-btn" title="Réduire / développer">
        <span class="caret" :class="{ rotated: isCollapsed }">▲</span>
      </button>

      <p class="question-text">
        <span class="node-type-badge">TP</span>
        <span class="textQuestioHeader"> {{ truncatedText || 'TextPuzzle sans texte' }}</span>
      </p>

      <div class="d-flex justify-content-between align-items-center">
        <button hidden class="btn btn-sm btn-outline-danger" @click.stop.prevent="onDelete">✕</button>
      </div>
    </div>

    <!-- Toolbar -->
    <div class="node-toolbar">
      <button class="toolbar-btn edit" @click="onOpenEditor" title="Éditer le contenu">
        <i class="fas fa-pen"></i>
      </button>

      <button class="toolbar-btn delete" @click.stop="onDelete" title="Supprimer le nœud">
        <i class="fas fa-trash"></i>
      </button>
    </div>

    <!-- Contenu du nœud avec animation -->
    <transition name="fade-slide">
      <div v-if="!isCollapsed" class="node-content">
        <div class="textpuzzle-info">
          <span class="badge bg-secondary">{{ gapsCount }} gaps</span>
          <span class="badge bg-primary ms-2">Max: {{ maxScore }} pts</span>
        </div>
      </div>
    </transition>

    <!-- Handle d'entrée (gauche) - masqué si nested -->
    <Handle
      v-if="!parentNode"
      id="input"
      type="target"
      :position="Position.Left"
      class="input-handle"
    />

    <!-- Handle de sortie SCORE uniquement (droite) - masqué si nested -->
    <Handle
      v-if="!parentNode"
      id="score"
      type="source"
      :position="Position.Right"
      class="score-handle"
      :is-valid-connection="isValidOutgoingConnection"
    >
      <span class="handle-label">S</span>
    </Handle>
  </div>
</template>

<script setup>
import { computed, ref, watch, nextTick } from 'vue'
import { Handle, Position } from '@vue-flow/core'
import { useTestStore } from '../../../stores/testStore'
import { useVueFlow } from '@vue-flow/core'

const { edges, setEdges, nodes } = useVueFlow()

const props = defineProps({
  id: String,
  data: { type: Object, default: () => ({}) },
  selected: Boolean,
  element: { type: Object, default: null },
  parentNode: { type: String, default: null }
})

const emit = defineEmits(['delete', 'update', 'open-editor'])
const testStore = useTestStore()

// --- Gestion du collapse (local + synchro)
const isCollapsed = ref(props.data?.collapsed ?? false)

watch(() => props.data?.collapsed, (val) => {
  isCollapsed.value = !!val
})

const toggleCollapse = async (event) => {
  event.stopPropagation()
  event.preventDefault()
  isCollapsed.value = !isCollapsed.value
  emit('update', { id: props.id, data: { collapsed: isCollapsed.value } })
  await nextTick()
}

const truncatedText = computed(() => {
  const text = props.element?.el_Text || 'TextPuzzle sans texte'
  if (!text) return ''
  return text.length > 30 ? text.slice(0, 30) + '…' : text
})

// --- Calcul du nombre de gaps et du score max
const gapsCount = computed(() => {
  return Array.isArray(props.element?.el_InteractionData?.gaps) ? props.element.el_InteractionData.gaps.length : 0
})

const maxScore = computed(() => {
  if (!Array.isArray(props.element?.el_InteractionData?.gaps)) return 0
  return props.element.el_InteractionData.gaps.reduce((sum, gap) => sum + (gap.weight || 0), 0)
})

// --- Actions
function onDelete() {
  if (window.confirm('⚠️ Are you sure you want to delete this node? \nThis action cannot be undone.')) {
    emit('delete', { nodeId: props.id, elementId: props.data?.elementId })
  }
}

function onOpenEditor() {
  emit('open-editor', { nodeId: props.id, elementId: props.data?.elementId })
}

// --- Sélection
const isSelected = computed(() => props.selected === true)

// --- Validation des connexions sortantes (seulement vers switcher en mode score)
function isValidOutgoingConnection(connection) {
  const allNodes = nodes.value || []
  const target = allNodes.find(n => n.id === connection.target)
  const targetType = target?.type

  // Mode SCORE : peut UNIQUEMENT se connecter à un switcher
  if (targetType !== 'switcher') {
    console.log('🚫 Connexion interdite : textPuzzle (mode score) ne peut se connecter qu\'à un switcher')
    return false
  }
  console.log('✅ Connexion autorisée : textPuzzle score → switcher')
  return true
}
</script>

<style scoped>
.question-node {
  background: white;
  border: 2px solid #ddd;
  border-radius: 8px;
  min-width: 300px;
  max-width: 400px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  position: relative;
  transition: all 0.3s ease;
}

.question-node.collapsed {
  min-width: 220px;
  max-width: 260px;
}

.question-node.selected {
  border-color: #0d6efd;
  box-shadow: 0 4px 12px rgba(13, 110, 253, 0.3);
  transform: translateY(-2px);
}

/* Header */
.node-header {
  background-color: #f8f9fa;
  padding: 10px;
  border-bottom: 1px solid #ddd;
  border-radius: 6px 6px 0 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.collapse-btn {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 12px;
  color: #666;
  padding: 4px 8px;
  border-radius: 3px;
  transition: all 0.2s ease;
}

.collapse-btn:hover {
  color: #0d6efd;
}

.caret {
  display: inline-block;
  transition: transform 0.25s ease;
  font-size: 12px;
}

.caret.rotated {
  transform: rotate(180deg);
}

/* Toolbar */
.node-toolbar {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
  margin-bottom: 4px;
  margin-right: 20px;
}

.toolbar-btn {
  border: none;
  background: #f8f9fa;
  color: #555;
  width: 26px;
  height: 26px;
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
}

.toolbar-btn:hover {
  background: #e9ecef;
  color: #0d6efd;
  transform: translateY(-1px);
}

.toolbar-btn:active {
  transform: scale(0.95);
}

/* Contenu */
.node-content {
  padding: 15px;
}

.textpuzzle-info {
  display: flex;
  gap: 8px;
  align-items: center;
}

/* Transition contenu */
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.3s ease;
}

.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

.fade-slide-enter-to,
.fade-slide-leave-from {
  opacity: 1;
  transform: translateY(0);
}

.question-text {
  position: absolute;
  margin-left: 30px;
  margin-top: 21px;
}

.textQuestioHeader {
  margin-left: 0px;
  color: #0056b3;
}

/* Handles */
.input-handle {
  width: 16px !important;
  height: 16px !important;
  background-color: #6c757d !important;
  border: 2px solid white !important;
  border-radius: 50% !important;
  transform: translateX(-50%);
}

.score-handle {
  width: 24px !important;
  height: 24px !important;
  background-color: #28a745 !important; /* vert */
  border: 2px solid white !important;
  border-radius: 6px !important;
  display: flex !important;
  align-items: center;
  justify-content: center;
  transform: translateX(50%);
  cursor: crosshair;
  color: white;
  font-weight: bold;
  transition: all 0.25s ease;
  top: 50%;
}

.score-handle:hover {
  background-color: #218838 !important;
  transform: translateX(50%) scale(1.1);
}

.handle-label {
  font-size: 11px;
  pointer-events: none;
}

.input-handle:hover {
  transform: translateX(-50%) scale(1.2);
}

/* Badge type TP */
.node-type-badge {
  display: inline-block;
  background: #6610f2;
  color: white;
  font-size: 10px;
  font-weight: bold;
  padding: 2px 5px;
  border-radius: 3px;
  margin-right: 6px;
}

.textpuzzle-node {
  border-color: #6610f2;
}

.textpuzzle-node.selected {
  border-color: #6610f2;
  box-shadow: 0 4px 12px rgba(102, 16, 242, 0.3);
}
</style>
